import { Button, DownloadFile } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import type {
  PayReportEventBreakdown,
  PayReportEventTotals
} from '../../utils/payReportDrawerTypes'

type Translate = (key: string, options?: Record<string, unknown>) => string

function formatBreakdown(
  breakdown: PayReportEventBreakdown,
  t: Translate
): string {
  const parts: string[] = []
  if (breakdown.plannedTime) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_PLANNED', {
        count: breakdown.plannedTime
      })
    )
  }
  if (breakdown.actualTime) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_ACTUAL', {
        count: breakdown.actualTime
      })
    )
  }
  if (breakdown.dontPay) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_DONT_PAY', { count: breakdown.dontPay })
    )
  }
  if (breakdown.customPaidTime) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_CUSTOM_TIME', {
        count: breakdown.customPaidTime
      })
    )
  }
  if (breakdown.customAmount) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_CUSTOM_AMOUNT', {
        count: breakdown.customAmount
      })
    )
  }
  if (breakdown.unknown) {
    parts.push(
      t('PAY_REPORT_EVENT_BREAKDOWN_UNKNOWN', { count: breakdown.unknown })
    )
  }
  return parts.join(', ')
}

type PayReportBreakdownFooterProps = {
  eventTotals: {
    visits: PayReportEventTotals
    shifts: PayReportEventTotals
    absences: PayReportEventTotals
  }
  t: Translate
}

export const PayReportBreakdownFooter = ({
  eventTotals,
  t
}: PayReportBreakdownFooterProps) => {
  const { visits, shifts, absences } = eventTotals

  const visitBreakdown = formatBreakdown(visits.breakdown, t)
  const shiftBreakdown = formatBreakdown(shifts.breakdown, t)
  const absenceBreakdown = formatBreakdown(absences.breakdown, t)

  return (
    <SummaryInline>
      <span>
        <Bold weight="medium">
          {t('PAY_REPORT_DRAWER_FOOTER_VISITS', { count: visits.count })}
        </Bold>
        {visitBreakdown ? <MutedInline> ({visitBreakdown})</MutedInline> : null}
      </span>
      {', '}
      <span>
        <Bold weight="medium">
          {t('PAY_REPORT_DRAWER_FOOTER_SHIFTS', { count: shifts.count })}
        </Bold>
        {shiftBreakdown ? <MutedInline> ({shiftBreakdown})</MutedInline> : null}
      </span>
      {', '}
      <span>
        <Bold weight="medium">
          {t('PAY_REPORT_DRAWER_FOOTER_ABSENCES', { count: absences.count })}
        </Bold>
        {absenceBreakdown ? (
          <MutedInline> ({absenceBreakdown})</MutedInline>
        ) : null}
      </span>
    </SummaryInline>
  )
}

type PayReportDrawerFooterBarProps = {
  eventTotals: PayReportBreakdownFooterProps['eventTotals']
  t: Translate
  onDownloadCsv: () => void
  csvLoading: boolean
}

export const PayReportDrawerFooterBar = ({
  eventTotals,
  t,
  onDownloadCsv,
  csvLoading
}: PayReportDrawerFooterBarProps) => {
  return (
    <FooterBar>
      <FooterSummary>
        <PayReportBreakdownFooter eventTotals={eventTotals} t={t} />
      </FooterSummary>
      <PayReportCsvButton
        data-testid="drawer-download-csv-btn"
        icon={<DownloadFile />}
        isLoading={csvLoading}
        onClick={onDownloadCsv}
      >
        {t('PAY_REPORT_DOWNLOAD_CSV')}
      </PayReportCsvButton>
    </FooterBar>
  )
}

const FooterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const FooterSummary = styled.div`
  flex: 1;
  min-width: 0;
`

const SummaryInline = styled(Text)`
  display: inline;
  color: ${({ theme }) => theme.color.neutral.color3};
  line-height: 1.5;
`

const Bold = styled(Text)`
  display: inline;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MutedInline = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export const PayReportCsvButton = styled(Button)`
  flex-shrink: 0;
`
